/**
 * Root SPA shell. Same Vite bundle, two production hostnames:
 *
 *   engage.paybyrd.com   → admin (engage) — Shell + sidebar
 *   partners.paybyrd.com → partner portal at the ROOT path
 *
 * On `partners.*` we suppress the admin route tree entirely and the
 * partner portal becomes the root surface (`/dashboard`, `/referrals`,
 * etc — no `/portal/` prefix). On `engage.*` (and dev / Railway-direct)
 * we keep the legacy `/portal/*` path-prefix so old bookmarks resolve,
 * but boot-time we 301 those to `partners.*` for strict separation.
 *
 * Both share the same Auth0 magic-link flow via LoginGate.
 */

import { Suspense, lazy, useEffect } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Route, Routes, Navigate } from "react-router-dom";

import { Shell } from "./shared/components/Shell";
import { LoginGate } from "./shared/components/LoginGate";
import { CommandPaletteProvider } from "./shared/components/CommandPalette";
import { AccountProvider } from "./shared/providers/AccountProvider";
import { PortalGate } from "./features/portal/components/PortalGate";
import { PortalShell } from "./features/portal/components/PortalShell";
import { isPartnerHost, partnerPortalUrl } from "./lib/hosts";

const PartnersPage = lazy(() => import("./features/engage/pages/PartnersPage"));
const PartnerDetailPage = lazy(() => import("./features/engage/pages/PartnerDetailPage"));
const VouchersPage = lazy(() => import("./features/engage/pages/VouchersPage"));
const RedemptionsPage = lazy(() => import("./features/engage/pages/RedemptionsPage"));
const PayoutsPage = lazy(() => import("./features/engage/pages/PayoutsPage"));
const OverviewPage = lazy(() => import("./features/engage/pages/OverviewPage"));
const AuditLogPage = lazy(() => import("./features/engage/pages/AuditLogPage"));
const RulesPage = lazy(() => import("./features/engage/pages/RulesPage"));

const PortalDashboardPage = lazy(
  () => import("./features/portal/pages/PortalDashboardPage"),
);
const PortalReferralsPage = lazy(
  () => import("./features/portal/pages/PortalReferralsPage"),
);
const PortalPayoutsPage = lazy(
  () => import("./features/portal/pages/PortalPayoutsPage"),
);
const PortalMarketingKitPage = lazy(
  () => import("./features/portal/pages/PortalMarketingKitPage"),
);
const PortalKycPage = lazy(
  () => import("./features/portal/pages/PortalKycPage"),
);

function PageLoader() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
      <CircularProgress />
    </Box>
  );
}

/**
 * On engage.paybyrd.com, redirect any leftover `/portal/*` URLs to
 * the dedicated partners.* host so old bookmarks keep working but the
 * "engage = admin only" mental model holds. No-op on partners.* and
 * in dev (where we still want path-prefix routing).
 */
function useLegacyPortalRedirect() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const { hostname, pathname, search, hash } = window.location;
    if (!hostname.startsWith("engage.")) return;
    if (!pathname.startsWith("/portal")) return;
    const rest = pathname.replace(/^\/portal/, "") || "/dashboard";
    const target = partnerPortalUrl(rest) + search + hash;
    window.location.replace(target);
  }, []);
}

export function App() {
  useLegacyPortalRedirect();
  const partnerHost = isPartnerHost();
  return (
    <LoginGate>
      {partnerHost ? <PartnerHostArea /> : <DualHostArea />}
    </LoginGate>
  );
}

/**
 * partners.paybyrd.com — partner portal at the root, admin tree omitted.
 * Routes match the engage.* `/portal/*` paths but without the prefix.
 */
function PartnerHostArea() {
  return (
    <PortalGate>
      <PortalShell>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<PortalDashboardPage />} />
            <Route path="/referrals" element={<PortalReferralsPage />} />
            <Route path="/payouts" element={<PortalPayoutsPage />} />
            <Route path="/marketing-kit" element={<PortalMarketingKitPage />} />
            <Route path="/kyc" element={<PortalKycPage />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </PortalShell>
    </PortalGate>
  );
}

/**
 * engage.paybyrd.com / dev / Railway-direct — admin tree at root,
 * legacy `/portal/*` still mounted (the redirect hook will bounce real
 * users to partners.* on production engage.* host, but dev keeps it).
 */
function DualHostArea() {
  return (
    <Routes>
      <Route path="/portal/*" element={<PortalArea />} />
      <Route path="/*" element={<AdminArea />} />
    </Routes>
  );
}

function PortalArea() {
  return (
    <PortalGate>
      <PortalShell>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<PortalDashboardPage />} />
            <Route path="referrals" element={<PortalReferralsPage />} />
            <Route path="payouts" element={<PortalPayoutsPage />} />
            <Route path="marketing-kit" element={<PortalMarketingKitPage />} />
            <Route path="kyc" element={<PortalKycPage />} />
            <Route path="*" element={<Navigate to="dashboard" replace />} />
          </Routes>
        </Suspense>
      </PortalShell>
    </PortalGate>
  );
}

function AdminArea() {
  return (
    <AccountProvider>
    <CommandPaletteProvider>
      <Shell>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Navigate to="/overview" replace />} />
            <Route path="/overview" element={<OverviewPage />} />
            <Route path="/partners" element={<PartnersPage />} />
            <Route path="/partners/:partnerId" element={<PartnerDetailPage />} />
            <Route path="/vouchers" element={<VouchersPage />} />
            <Route path="/redemptions" element={<RedemptionsPage />} />
            <Route path="/payouts" element={<PayoutsPage />} />
            <Route path="/rules" element={<RulesPage />} />
            <Route path="/audit" element={<AuditLogPage />} />
            <Route path="*" element={<Navigate to="/overview" replace />} />
          </Routes>
        </Suspense>
      </Shell>
    </CommandPaletteProvider>
    </AccountProvider>
  );
}
